import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Switch, FormControlLabel, IconButton, Divider,
  Accordion, AccordionSummary, AccordionDetails, Tabs, Tab, CircularProgress,
  Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { useCompetitions } from '../CompetitionContext';

const LeagueManager = () => {
  const { competitions } = useCompetitions();
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [metadata, setMetadata] = useState({ regions: [], districts: [] });
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const allLeaguesList = Object.values(competitions).flat()
    .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));

  useEffect(() => {
    if (!selectedLeagueId) return;
    
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/league-manager/${selectedLeagueId}`)
      .then(res => {
        setLeagueDetails(res.data);
        if (res.data.metadata) setMetadata(res.data.metadata);
      })
      .catch(err => console.error("Błąd ładowania danych ligi:", err))
      .finally(() => setLoading(false));
  }, [selectedLeagueId]);

  const handleSave = async () => {
    if (!selectedLeagueId) return;
    setSaveLoading(true);

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/league-manager/${selectedLeagueId}`, leagueDetails);
      alert("Wszystkie zmiany zostały zapisane!");
    } catch (err) {
      console.error("Błąd zapisu:", err);
      alert("Błąd zapisu: " + (err.response?.data?.error || err.message));
    } finally {
      setSaveLoading(false);
    }
  };

  const updateField = (field, value) => {
    setLeagueDetails(prev => ({ ...prev, [field]: value }));
  };

  // --- LOGIKA KREATORA BARAŻY ---
  const addPlayoffTemplate = () => {
    const newTpl = { id: 0, name: "Nowy Szablon Barażowy", draw_required: true, rounds: [] };
    setLeagueDetails(prev => ({ ...prev, playoff_templates: [...(prev.playoff_templates || []), newTpl] }));
  };

  const removeTemplate = (tIndex) => {
    if(window.confirm("Na pewno usunąć ten szablon?")) {
        const up = { ...leagueDetails };
        up.playoff_templates.splice(tIndex, 1);
        setLeagueDetails(up);
    }
  };

  const addRound = (tIndex) => {
    const up = { ...leagueDetails };
    const rounds = up.playoff_templates[tIndex].rounds || [];
    rounds.push({ round_name: "Runda " + (rounds.length + 1), order_index: rounds.length + 1, is_double_leg: false, participants: [] });
    up.playoff_templates[tIndex].rounds = rounds;
    setLeagueDetails(up);
  };

  const removeRound = (tIndex, rIndex) => {
    const up = { ...leagueDetails };
    up.playoff_templates[tIndex].rounds.splice(rIndex, 1);
    // Przeliczanie kolejności
    up.playoff_templates[tIndex].rounds.forEach((r, idx) => r.order_index = idx + 1);
    setLeagueDetails(up);
  };

  const addParticipant = (tIndex, rIndex) => {
    const up = { ...leagueDetails };
    const parts = up.playoff_templates[tIndex].rounds[rIndex].participants || [];
    parts.push({ source_competition_id: selectedLeagueId, pos_start: 2, pos_end: 2, bracket_group: 1 });
    up.playoff_templates[tIndex].rounds[rIndex].participants = parts;
    setLeagueDetails(up);
  };

  const removeParticipant = (tIndex, rIndex, pIndex) => {
    const up = { ...leagueDetails };
    up.playoff_templates[tIndex].rounds[rIndex].participants.splice(pIndex, 1);
    setLeagueDetails(up);
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" mb={3} fontWeight="bold">League Manager Pro</Typography>

      {/* WYBÓR LIGI - AUTOCOMPLETE */}
      <Paper sx={{ p: 2, mb: 4, bgcolor: '#1e1e1e' }}>
        <Autocomplete
          options={allLeaguesList}
          getOptionLabel={(option) => `[Lvl ${option.level}] ${option.name} ${option.group || ''}`}
          value={allLeaguesList.find(l => l.competition_id === selectedLeagueId) || null}
          onChange={(event, newValue) => setSelectedLeagueId(newValue ? newValue.competition_id : '')}
          renderInput={(params) => <TextField {...params} label="Wyszukaj ligę do edycji..." variant="outlined" />}
          fullWidth
        />
      </Paper>

      {leagueDetails && (
        <Paper sx={{ p: 0, overflow: 'hidden', bgcolor: '#1e1e1e' }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333' }}>
            <Box>
                <Typography variant="h5" color="primary">{leagueDetails.name} {leagueDetails.group}</Typography>
                <Typography variant="caption" color="textSecondary">ID: {selectedLeagueId} | Sezon: {leagueDetails.season}</Typography>
            </Box>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<SaveIcon />} 
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? "Zapisywanie..." : "Zapisz Wszystkie Zmiany"}
            </Button>
          </Box>

          <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ px: 2, mt: 1 }}>
            <Tab label="Podstawowe" />
            <Tab label="Awanse / Spadki" />
            <Tab label="Baraże" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* TAB 0: PODSTAWOWE (Z poprzedniej wiadomości) */}
            {tabIndex === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Nazwa ligi" value={leagueDetails.name || ''} onChange={e => updateField('name', e.target.value)} sx={{ mb: 2 }} />
                  <TextField fullWidth label="Grupa" value={leagueDetails.group || ''} onChange={e => updateField('group', e.target.value)} sx={{ mb: 2 }} />
                  <TextField fullWidth label="Poziom (Lvl)" type="number" value={leagueDetails.level || 0} onChange={e => updateField('level', parseInt(e.target.value))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Region</InputLabel>
                    <Select value={leagueDetails.region_id || 0} label="Region" onChange={e => updateField('region_id', e.target.value)}>
                      <MenuItem value={0}>Brak / Krajowy</MenuItem>
                      {metadata.regions.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>District (Okręg)</InputLabel>
                    <Select value={leagueDetails.district_id || 0} label="District" onChange={e => updateField('district_id', e.target.value)}>
                      <MenuItem value={0}>Brak</MenuItem>
                      {metadata.districts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Awans do (Liga Wyżej)</InputLabel>
                    <Select value={leagueDetails.promoted_competition_id || 0} label="Awans do (Liga Wyżej)" onChange={e => updateField('promoted_competition_id', e.target.value)}>
                      <MenuItem value={0}>Brak</MenuItem>
                      {allLeaguesList.filter(l => l.level < leagueDetails.level).map(l => (
                        <MenuItem key={l.competition_id} value={l.competition_id}>{l.name} {l.group}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Wymagania i Linki</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Link (90minut)" value={leagueDetails.link || ''} onChange={e => updateField('link', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Wymóg Statystyk" value={leagueDetails.stats_requirement || ''} onChange={e => updateField('stats_requirement', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Pełny Wymóg danych" value={leagueDetails.full_requirement || ''} onChange={e => updateField('full_requirement', e.target.value)} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* TAB 1: AWANSE I SPADKI (Z poprzedniej wiadomości) */}
            {tabIndex === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography color="success.main" gutterBottom fontWeight="bold">Strefa Awansu</Typography>
                  <TextField fullWidth type="number" label="Awanse bezpośrednie" value={leagueDetails.promotion_spots || 0} onChange={e => updateField('promotion_spots', parseInt(e.target.value))} sx={{ mb: 2 }} />
                  <TextField fullWidth type="number" label="Miejsca w barażach" value={leagueDetails.promotion_playoff_spots || 0} onChange={e => updateField('promotion_playoff_spots', parseInt(e.target.value))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="error.main" gutterBottom fontWeight="bold">Strefa Spadku</Typography>
                  <TextField fullWidth type="number" label="Spadki bezpośrednie" value={leagueDetails.relegation_spots || 0} onChange={e => updateField('relegation_spots', parseInt(e.target.value))} sx={{ mb: 2 }} />
                  <TextField fullWidth type="number" label="Baraże o utrzymanie" value={leagueDetails.relegation_playoff_spots || 0} onChange={e => updateField('relegation_playoff_spots', parseInt(e.target.value))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Divider sx={{ my: 2 }} />
                  <Typography gutterBottom fontWeight="bold">Kaskada i Rozmiar</Typography>
                  <TextField fullWidth type="number" label="Docelowy rozmiar ligi" value={leagueDetails.target_size || 0} onChange={e => updateField('target_size', parseInt(e.target.value))} sx={{ mb: 2 }} />
                  <FormControlLabel control={<Switch checked={leagueDetails.is_balancing_active || false} onChange={e => updateField('is_balancing_active', e.target.checked)} />} label="Aktywuj kaskadę" />
                </Grid>
              </Grid>
            )}

            {/* TAB 2: KREATOR BARAŻY (Ulepszony) */}
            {tabIndex === 2 && (
              <Box>
                <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addPlayoffTemplate} sx={{ mb: 3 }}>
                  Dodaj Nowy Szablon Barażowy
                </Button>

                {leagueDetails.playoff_templates?.map((tpl, tIndex) => (
                  <Accordion key={tIndex} defaultExpanded sx={{ mb: 3, bgcolor: '#252525' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderLeft: '4px solid #1976d2' }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>{tpl.name} (ID: {tpl.id || 'Nowy'})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      
                      {/* EDYCJA SZABLONU */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, p: 2, bgcolor: '#2a2a2a', borderRadius: 1 }}>
                        <TextField label="Nazwa Szablonu" size="small" sx={{ flexGrow: 1 }} value={tpl.name} onChange={e => {
                          const up = {...leagueDetails}; up.playoff_templates[tIndex].name = e.target.value; setLeagueDetails(up);
                        }} />
                        <FormControlLabel control={<Switch checked={tpl.draw_required ?? true} onChange={e => {
                          const up = {...leagueDetails}; up.playoff_templates[tIndex].draw_required = e.target.checked; setLeagueDetails(up);
                        }} />} label="Losowanie par w turnieju" />
                        <Button color="error" onClick={() => removeTemplate(tIndex)}><DeleteIcon /> Usuń szablon</Button>
                      </Box>

                      {/* EDYCJA RUND */}
                      {tpl.rounds?.sort((a, b) => a.order_index - b.order_index).map((rnd, rIndex) => (
                        <Paper key={rIndex} sx={{ p: 2, mb: 2, bgcolor: '#333' }}>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 60, color: 'primary.light' }}>Runda {rnd.order_index}</Typography>
                            <TextField label="Nazwa rundy" size="small" sx={{ flexGrow: 1 }} value={rnd.round_name} onChange={e => {
                               const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].round_name = e.target.value; setLeagueDetails(up);
                            }}/>
                            <FormControlLabel control={<Switch size="small" checked={rnd.is_double_leg ?? false} onChange={e => {
                               const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].is_double_leg = e.target.checked; setLeagueDetails(up);
                            }} />} label="Dwumecz" />
                            <IconButton color="error" onClick={() => removeRound(tIndex, rIndex)}><DeleteIcon /></IconButton>
                          </Box>

                          {/* UCZESTNICY (Źródła) */}
                          <Box sx={{ pl: 4, borderLeft: '2px solid #555' }}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>Uczestnicy (Skąd pobierać drużyny):</Typography>
                            {rnd.participants?.map((part, pIndex) => (
                              <Box key={pIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Select size="small" sx={{ flexGrow: 1, minWidth: 150 }} value={part.source_competition_id} onChange={e => {
                                   const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].source_competition_id = e.target.value; setLeagueDetails(up);
                                }}>
                                  {allLeaguesList.map(l => <MenuItem key={l.competition_id} value={l.competition_id}>[Lvl {l.level}] {l.name} {l.group}</MenuItem>)}
                                </Select>
                                <TextField label="Od poz." type="number" size="small" sx={{ width: 40 }} value={part.pos_start} onChange={e => {
                                   const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].pos_start = parseInt(e.target.value) || 0; setLeagueDetails(up);
                                }}/>
                                <TextField label="Do poz." type="number" size="small" sx={{ width: 40 }} value={part.pos_end} onChange={e => {
                                   const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].pos_end = parseInt(e.target.value) || 0; setLeagueDetails(up);
                                }}/>
                                <TextField label="Koszyk (Drabinka)" type="number" size="small" sx={{ width: 40 }} value={part.bracket_group ?? 1} onChange={e => {
                                   const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].bracket_group = parseInt(e.target.value) || 1; setLeagueDetails(up);
                                }}/>
                                <IconButton color="error" onClick={() => removeParticipant(tIndex, rIndex, pIndex)}><DeleteIcon fontSize="small"/></IconButton>
                              </Box>
                            ))}
                            <Button size="small" sx={{ mt: 1 }} onClick={() => addParticipant(tIndex, rIndex)}>+ Dodaj Źródło (Kluby)</Button>
                          </Box>
                        </Paper>
                      ))}
                      <Button variant="outlined" size="small" onClick={() => addRound(tIndex)}>+ Dodaj Nową Rundę Barażową</Button>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default LeagueManager;